/**
 * Client Portal - Job Request Page
 * Simplified intake form for clients to request new jobs via intake API.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Zap, Flame, Thermometer, Droplet, Waves, Bell, Droplets,
  Building, Wrench, AlertTriangle, Calendar, FileText, Search,
  HelpCircle, Plus, Eye, Loader2, CheckCircle2, Clock, User,
  Phone, Mail, MapPin, Camera, X, ArrowLeft, Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Issue type icons
const ISSUE_ICONS = {
  electrical_fault: Zap,
  gas_issue: Flame,
  boiler_issue: Thermometer,
  plumbing_leak: Droplet,
  drainage_issue: Waves,
  fire_alarm_issue: Bell,
  legionella_issue: Droplets,
  structural_issue: Building,
  general_repair: Wrench,
  damage_report: AlertTriangle,
  ppm_task: Calendar,
  quoted_work: FileText,
  inspection_required: Search,
  unknown_other: HelpCircle
};

// Priority colors
const PRIORITY_COLORS = {
  urgent: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-slate-900',
  low: 'bg-green-500 text-white'
};

export default function ClientJobRequestPage() {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    park_id: '',
    park_name: '',
    unit_name: '',
    issue_type: '',
    description: '',
    priority: 'medium',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    access_notes: '',
    source_type: 'client_portal_request'
  });
  
  // Options and state
  const [options, setOptions] = useState({
    issue_types: [],
    priorities: [],
    parks: [],
    contractors: []
  });
  
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [createdJob, setCreatedJob] = useState(null);
  const [parkSearch, setParkSearch] = useState('');
  const [showParkDropdown, setShowParkDropdown] = useState(false);
  const [photos, setPhotos] = useState([]);
  
  // Get client info from token
  const [clientInfo, setClientInfo] = useState(null);

  useEffect(() => {
    fetchOptions();
    fetchClientInfo();
  }, []);

  const fetchClientInfo = async () => {
    try {
      // Try client portal token first, then fall back to regular token
      const token = localStorage.getItem('client_portal_token') || localStorage.getItem('token');
      if (!token) return;
      
      // Decode token to get client info
      const payload = JSON.parse(atob(token.split('.')[1]));
      setClientInfo({
        id: payload.client_id || payload.id,
        name: payload.name,
        email: payload.email
      });
      
      // Pre-fill contact info
      setFormData(prev => ({
        ...prev,
        contact_name: payload.name || '',
        contact_email: payload.email || ''
      }));
    } catch (err) {
      console.error('Failed to decode client info:', err);
    }
  };

  const fetchOptions = async () => {
    try {
      // Try client portal token first, then fall back to regular token
      const token = localStorage.getItem('client_portal_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/api/jobs/intake/options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        // Filter to common issue types for client portal
        const clientIssueTypes = response.data.issue_types.filter(t => 
          ['electrical_fault', 'gas_issue', 'boiler_issue', 'plumbing_leak', 
           'drainage_issue', 'fire_alarm_issue', 'general_repair', 'damage_report',
           'unknown_other'].includes(t.value)
        );
        
        setOptions({
          ...response.data,
          issue_types: clientIssueTypes
        });
      }
    } catch (err) {
      console.error('Failed to fetch options:', err);
      toast.error('Failed to load form options');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.park_id || !formData.issue_type || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setPreviewing(true);
    setPreview(null);
    
    try {
      // Try client portal token first, then fall back to regular token
      const token = localStorage.getItem('client_portal_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/api/jobs/intake/preview`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        setPreview(response.data);
      } else {
        toast.error(response.data.error || 'Preview failed');
      }
    } catch (err) {
      console.error('Preview error:', err);
      toast.error(err.response?.data?.detail || 'Failed to preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.park_id || !formData.issue_type || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    
    try {
      // Try client portal token first, then fall back to regular token
      const token = localStorage.getItem('client_portal_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/api/jobs/intake/create`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        setCreatedJob(response.data);
        setSubmitted(true);
        toast.success(`Request submitted: ${response.data.job_reference}`);
      } else if (response.data.require_duplicate_override) {
        if (window.confirm(`${response.data.error}\n\nSubmit anyway?`)) {
          const retryResponse = await axios.post(`${API}/api/jobs/intake/create`, {
            ...formData,
            override_duplicate_warning: true
          }, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (retryResponse.data.ok) {
            setCreatedJob(retryResponse.data);
            setSubmitted(true);
            toast.success(`Request submitted: ${retryResponse.data.job_reference}`);
          } else {
            toast.error(retryResponse.data.error || 'Failed to submit');
          }
        }
      } else {
        toast.error(response.data.error || 'Failed to submit');
      }
    } catch (err) {
      console.error('Create error:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setCreating(false);
    }
  };

  // Photo handling
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setPhotos([...photos, ...newPhotos]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  // Filter parks by search
  const filteredParks = parkSearch
    ? options.parks.filter(p => 
        p.name?.toLowerCase().includes(parkSearch.toLowerCase()) ||
        p.postcode?.toLowerCase().includes(parkSearch.toLowerCase())
      )
    : options.parks;

  const selectPark = (park) => {
    setFormData(prev => ({
      ...prev,
      park_id: park.id,
      park_name: park.name
    }));
    setParkSearch(park.name);
    setShowParkDropdown(false);
  };

  // Success screen
  if (submitted && createdJob) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="client-job-request-success">
        <div className="max-w-lg mx-auto px-4 py-16">
          <Card className="border-0 shadow-lg text-center">
            <CardContent className="pt-8 pb-8 space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted!</h1>
                <p className="text-slate-600">Your job request has been logged and will be reviewed by our team.</p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4 text-left">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Reference</span>
                    <span className="font-mono font-bold text-[#1F7A78]">{createdJob.job_reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <Badge className="bg-blue-100 text-blue-700">{createdJob.status}</Badge>
                  </div>
                  {createdJob.due_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Expected</span>
                      <span className="text-slate-700">
                        {new Date(createdJob.due_date).toLocaleDateString('en-GB', {
                          weekday: 'short', day: 'numeric', month: 'short'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-3 pt-4">
                <Button 
                  onClick={() => navigate('/client/dashboard')}
                  className="w-full bg-[#1F7A78] hover:bg-[#005f5f]"
                >
                  View Dashboard
                </Button>
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setCreatedJob(null);
                    setPreview(null);
                    setFormData({
                      park_id: '',
                      park_name: '',
                      unit_name: '',
                      issue_type: '',
                      description: '',
                      priority: 'medium',
                      contact_name: clientInfo?.name || '',
                      contact_phone: '',
                      contact_email: clientInfo?.email || '',
                      access_notes: '',
                      source_type: 'client_portal_request'
                    });
                    setParkSearch('');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Submit Another Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-job-request-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#1F7A78] flex items-center justify-center">
              <Send className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">New Job Request</h1>
              <p className="text-sm text-slate-500">Submit a maintenance request</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Form Card */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="pt-6 space-y-5">
            
            {/* Site Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Site / Park <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search sites..."
                  value={parkSearch}
                  onChange={(e) => {
                    setParkSearch(e.target.value);
                    setShowParkDropdown(true);
                    if (!e.target.value) {
                      setFormData(prev => ({ ...prev, park_id: '', park_name: '' }));
                    }
                  }}
                  onFocus={() => setShowParkDropdown(true)}
                  className="pl-10"
                  data-testid="client-park-search"
                />
                {showParkDropdown && filteredParks.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {filteredParks.slice(0, 8).map(park => (
                      <div
                        key={park.id}
                        className="px-4 py-2 hover:bg-slate-50 cursor-pointer"
                        onClick={() => selectPark(park)}
                      >
                        <div className="font-medium text-slate-800">{park.name}</div>
                        {park.postcode && (
                          <div className="text-xs text-slate-500">{park.postcode}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Unit */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Unit / Plot
              </label>
              <Input
                type="text"
                placeholder="e.g. Plot 42, Unit 7"
                value={formData.unit_name}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_name: e.target.value }))}
                data-testid="client-unit-input"
              />
            </div>
            
            {/* Issue Type Grid */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Issue Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {options.issue_types.map(type => {
                  const Icon = ISSUE_ICONS[type.value] || HelpCircle;
                  const isSelected = formData.issue_type === type.value;
                  
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, issue_type: type.value }))}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-[#1F7A78] bg-[#1F7A78]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`client-issue-${type.value}`}
                    >
                      <Icon className={`h-5 w-5 ${isSelected ? 'text-[#1F7A78]' : 'text-slate-500'}`} />
                      <span className={`text-xs font-medium text-center ${isSelected ? 'text-[#1F7A78]' : 'text-slate-600'}`}>
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Describe the issue..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#1F7A78] focus:border-transparent resize-none"
                data-testid="client-description-input"
              />
            </div>
            
            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Priority
              </label>
              <div className="flex gap-2">
                {options.priorities.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, priority: p.value }))}
                    className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      formData.priority === p.value
                        ? PRIORITY_COLORS[p.value]
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`client-priority-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Photos */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Photos (Optional)
              </label>
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#1F7A78] transition-colors">
                <Camera className="h-5 w-5 text-slate-400" />
                <span className="text-sm text-slate-600">Add photos</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="client-photo-upload"
                />
              </label>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative w-16 h-16">
                      <img 
                        src={photo.preview} 
                        alt={photo.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3 pt-3 border-t border-slate-100">
              <label className="block text-sm font-medium text-slate-700">Contact Details</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Input
                    type="text"
                    placeholder="Name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  />
                </div>
              </div>
              <Input
                type="text"
                placeholder="Access notes (e.g. key code, entry instructions)"
                value={formData.access_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, access_notes: e.target.value }))}
              />
            </div>
            
          </CardContent>
        </Card>
        
        {/* Preview Panel */}
        {preview && (
          <Card className="bg-[#1F7A78]/5 border-[#1F7A78]/20" data-testid="client-preview-panel">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-[#1F7A78] text-base">
                <Eye className="h-5 w-5" />
                Request Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white p-3 rounded-xl">
                  <div className="text-xs text-slate-500">Type</div>
                  <div className="font-medium text-slate-800 text-sm">{preview.classification?.job_type}</div>
                </div>
                <div className="bg-white p-3 rounded-xl">
                  <div className="text-xs text-slate-500">Expected By</div>
                  <div className="font-medium text-slate-800 text-sm">
                    {preview.due_dates?.due_date 
                      ? new Date(preview.due_dates.due_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short' 
                        })
                      : '-'}
                  </div>
                </div>
              </div>
              
              {preview.context?.duplicate_warning && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700">{preview.context.duplicate_message}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handlePreview}
            disabled={previewing || !formData.park_id || !formData.issue_type}
            variant="outline"
            className="flex-1 gap-2"
            data-testid="client-preview-btn"
          >
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            Preview
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !formData.park_id || !formData.issue_type || !formData.description}
            className="flex-1 bg-[#1F7A78] hover:bg-[#005f5f] gap-2"
            data-testid="client-submit-btn"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit Request
          </Button>
        </div>
        
      </div>
    </div>
  );
}
